import { IssueStatus, IssueType, ProjectIssue } from "@/generated/graphql";

import {
  blocked,
  completed,
  in_progress,
  not_started,
} from "./components/issue";
import { ProjectIssueWithChildren } from "./types";

/** Parse the Project Issues into a more nested structure with children Project Issues */
export function nestProjectIssues(
  issues: ProjectIssue[]
): ProjectIssueWithChildren[] {
  const createIssueMap = (issues: ProjectIssue[]) =>
    issues.reduce(
      (map, issue) => ({
        ...map,
        [issue.id]: { ...issue, children: [] },
      }),
      {} as Record<string, ProjectIssueWithChildren>
    );

  const buildHierarchy = (map: Record<string, ProjectIssueWithChildren>) => {
    const result = { ...map };

    Object.values(result).forEach((issue) => {
      if (issue.parentId) {
        const parent = result[issue.parentId];
        if (parent) {
          parent.children.push(issue);
        }
      }
    });

    return result;
  };

  const sortByDueDate = (
    a: ProjectIssueWithChildren,
    b: ProjectIssueWithChildren
  ) => {
    const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
    const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
    return aDate - bDate;
  };

  const issueMap = createIssueMap(issues);
  const hierarchyMap = buildHierarchy(issueMap);

  // Sort children of each parent by dueDate ascending
  Object.values(hierarchyMap).forEach((issue) => {
    if (issue.children?.length) {
      issue.children.sort(sortByDueDate);
    }
  });

  // Return top-level items sorted by dueDate ascending
  return Object.values(hierarchyMap)
    .filter((issue) => !issue.parentId)
    .sort(sortByDueDate);
}

/** for handling date in issue form  */
export const getDateString = (date: string | null) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0]!;
};

export const getStatusBadgeStyles = (status: string) => {
  switch (status) {
    case IssueStatus.NotStarted:
      return not_started;
    case IssueStatus.Blocked:
      return blocked;
    case IssueStatus.Completed:
      return completed;
    case IssueStatus.InProgress:
      return in_progress;
    default:
      return "";
  }
};

// Check if a date is overdue
export const isOverdue = (dateString: string | null) => {
  if (!dateString) return false;
  const dueDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
  return dueDate < today;
};

// for dropdowns
export const statusOptions = Object.values(IssueStatus).map((status) => ({
  value: status,
  text: status.replace("_", " "),
}));

// for dropdowns
export const typeOptions = Object.values(IssueType).map((type) => ({
  value: type,
  text: type,
}));
